/**
 * Scales the LEADING quantity of each ingredient line. Understands integers, decimals, fractions (1/2),
 * mixed numbers (1 1/2), unicode fractions (½, 1½) and ranges (2-3, 2 to 3). Numbers later in the line
 * (e.g. "(14 oz)" package sizes) are left alone.
 */
const UNICODE: Record<string, number> = { "¼": 0.25, "½": 0.5, "¾": 0.75, "⅓": 1 / 3, "⅔": 2 / 3, "⅕": 0.2, "⅖": 0.4, "⅗": 0.6, "⅘": 0.8, "⅙": 1 / 6, "⅚": 5 / 6, "⅛": 0.125, "⅜": 0.375, "⅝": 0.625, "⅞": 0.875 };
const UNI = Object.keys(UNICODE).join("");
const NUM = `(?:\\d+\\s+\\d+\\/\\d+|\\d+\\/\\d+|\\d+(?:[.,]\\d+)?\\s?[${UNI}]|\\d*[.,]?\\d+|[${UNI}])`;
const LEAD = new RegExp(`^(\\s*(?:[-*•]\\s+)?)(${NUM})(?:\\s*(?:-|–|—|to)\\s*(${NUM}))?(?![\\d/])`, "i");

export function parseQuantity(s: string): number | null {
  const t = s.trim();
  let m: RegExpExecArray | null;
  if ((m = /^(\d+)\s+(\d+)\/(\d+)$/.exec(t))) return Number(m[3]) ? Number(m[1]) + Number(m[2]) / Number(m[3]) : null;
  if ((m = /^(\d+)\/(\d+)$/.exec(t))) return Number(m[2]) ? Number(m[1]) / Number(m[2]) : null;
  if ((m = new RegExp(`^(\\d+(?:[.,]\\d+)?)?\\s?([${UNI}])$`).exec(t))) return (m[1] ? Number(m[1].replace(",", ".")) : 0) + UNICODE[m[2]];
  if (/^\d*[.,]?\d+$/.test(t)) return Number(t.replace(",", "."));
  return null;
}

const FRACTIONS: [number, string][] = [[0, ""], [1 / 8, "⅛"], [1 / 4, "¼"], [1 / 3, "⅓"], [3 / 8, "⅜"], [1 / 2, "½"], [5 / 8, "⅝"], [2 / 3, "⅔"], [3 / 4, "¾"], [7 / 8, "⅞"], [1, ""]];

/** 2.25 -> "2¼", 0.75 -> "¾". Falls back to up to 2 decimals when nothing is within 1/16 of a nice fraction. */
export function formatQuantity(n: number): string {
  if (!Number.isFinite(n) || n < 0) return String(n);
  const whole = Math.floor(n), frac = n - whole;
  let best = FRACTIONS[0], bd = Infinity;
  for (const f of FRACTIONS) { const d = Math.abs(frac - f[0]); if (d < bd) { bd = d; best = f; } }
  if (bd <= 1 / 32) {
    const w = whole + (best[0] === 1 ? 1 : 0);
    const f = best[0] === 1 ? "" : best[1];
    return w === 0 && !f ? "0" : `${w || ""}${f}`;
  }
  return String(Math.round(n * 100) / 100);
}

export interface ScaledLine { text: string; scaled: boolean; note?: string }

const UNIT_PLURALS: Record<string, string> = { cup: "cups", tablespoon: "tablespoons", teaspoon: "teaspoons", tbsp: "tbsp", tsp: "tsp", clove: "cloves", can: "cans", slice: "slices", egg: "eggs", stick: "sticks", pinch: "pinches", ounce: "ounces", pound: "pounds", package: "packages" };

function adjustUnit(rest: string, plural: boolean): string {
  const pre = /^\s*\([^)]*\)/.exec(rest)?.[0] ?? "";
  if (pre) return pre + adjustUnit(rest.slice(pre.length), plural);
  const adj = /^(\s*(?:large|medium|small|extra[- ]large|jumbo|whole|ripe|fresh)\s+)(?=[A-Za-z])/i.exec(rest)?.[0] ?? "";
  if (adj) return adj + adjustUnit(rest.slice(adj.length), plural);
  const m = /^(\s*)([A-Za-z]+)(?=[\s,.)]|$)/.exec(rest);
  if (!m) return rest;
  const word = m[2].toLowerCase();
  const singular = Object.keys(UNIT_PLURALS).find((k) => k === word || UNIT_PLURALS[k] === word);
  if (!singular || singular === UNIT_PLURALS[singular]) return rest;
  const target = plural ? UNIT_PLURALS[singular] : singular;
  return m[1] + (m[2][0] === m[2][0].toUpperCase() ? target[0].toUpperCase() + target.slice(1) : target) + rest.slice(m[0].length);
}

export function scaleLine(line: string, factor: number): ScaledLine {
  const m = LEAD.exec(line);
  if (!m) return { text: line, scaled: false };
  const a = parseQuantity(m[2]);
  const b = m[3] ? parseQuantity(m[3]) : null;
  if (a === null || (m[3] && b === null)) return { text: line, scaled: false };
  const rest = line.slice(m[0].length);
  const sa = a * factor, sb = b === null ? null : b * factor;
  const metric = /^\s*(g|kg|mg|ml|l|dl|cl|oz|lb|lbs)\b/i.test(rest.replace(/^\s*\([^)]*\)/, ""));
  const f = (n: number) => (metric ? String(Math.round(n * 100) / 100) : formatQuantity(n));
  const shown = sb === null ? f(sa) : `${f(sa)}–${f(sb)}`;
  const plural = (sb ?? sa) > 1;
  // Awkward decimals: small cup amounts read better as tbsp + tsp, small litre amounts as ml.
  if (sb === null && sa > 0) {
    const cupM = /^(\s*)cups?\b/i.exec(rest);
    if (cupM && sa < 1 && formatQuantity(sa).includes(".")) {
      const tsp = Math.round(sa * 48);
      if (tsp >= 1) {
        const tb = Math.floor(tsp / 3), ts = tsp % 3;
        const parts = [tb ? `${tb} tbsp` : "", ts ? `${ts} tsp` : ""].filter(Boolean).join(" + ");
        return { text: `${m[1]}${parts}${rest.slice(cupM[0].length)}`, scaled: true };
      }
    }
    const lM = /^(\s*)l\b/i.exec(rest);
    if (lM && sa < 1) return { text: `${m[1]}${Math.round(sa * 1000)} ml${rest.slice(lM[0].length)}`, scaled: true };
  }
  let note: string | undefined;
  if (/^\s*(large\s+|medium\s+|small\s+)?eggs?\b/i.test(rest) && sb === null && Math.abs(sa - Math.round(sa)) > 0.01) note = "a part egg: beat one and use the right share, or round";
  return { text: `${m[1]}${shown}${adjustUnit(rest, plural)}`, scaled: true, note };
}

export function scaleRecipe(text: string, original: number, target: number): ScaledLine[] | { error: string } {
  if (!(original > 0) || !Number.isFinite(original)) return { error: "Original servings must be more than 0." };
  if (!(target > 0) || !Number.isFinite(target)) return { error: "New servings must be more than 0." };
  const factor = target / original;
  return text.split("\n").map((l) => scaleLine(l, factor));
}
